import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from "react";

import type { WheelSegment } from "./types.js";

const W_CX = 475;
const W_CY = 465;
const W_R = 425;
const W_INNER = 239.641;
const W_BASE_OP = 0.2;
const W_OPS = [0.1, 0.15, 0, 0.05, 0.15, 0.1, 0.05, 0];
const PIN_D =
  "M109 53C109 25.39 86.61 3 59 3C31.39 3 9 25.39 9 53C9 60.5 10.66 67.6 13.62 73.98L50.2 142.27C53.98 149.28 64.03 149.28 67.81 142.27L104.39 73.98C107.35 67.6 109.01 60.49 109.01 53H109Z";
const PIN_SCALE = 1.55;

function labelText(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") return "-";
  return trimmed.length > 14 ? `${trimmed.slice(0, 13)}...` : trimmed;
}

/**
 * How far a label is turned inside the disc, so it rides the spin and still
 * comes to rest the right way up.
 *
 * The label is a child of the spinning group, so its own transform is the
 * slice's mid angle, plus a half turn when that slice will settle on the left
 * half of the face where an unflipped label would read upside down. The flip is
 * decided from the angle the slice is travelling TO rather than the one it is
 * leaving, so it is already in place before the animation starts: the text
 * turns with the wheel throughout and nothing snaps round at the end.
 */
function labelTilt(mid: number, turn: number): number {
  const resting = ((mid + turn) % 360 + 360) % 360;
  return resting > 90 && resting < 270 ? mid + 180 : mid;
}

export function WheelDial({
  segments,
  spinning,
  landed,
  onFlick,
  showLabels,
}: {
  readonly segments: readonly WheelSegment[];
  readonly spinning: boolean;
  readonly landed: number | null;
  readonly onFlick: (() => void) | null;
  readonly showLabels: boolean;
}): ReactNode {
  const n = Math.max(4, Math.min(12, segments.length));
  const step = 360 / n;
  const gRef = useRef<SVGGElement | null>(null);
  const drag = useRef<{ x: number; y: number; t: number } | null>(null);
  const [turn, setTurn] = useState(0);

  useEffect(() => {
    const g = gRef.current;
    if (g === null) return undefined;
    if (landed === null) {
      g.style.transition = "none";
      g.style.transform = "rotate(0deg)";
      setTurn(0);
      return undefined;
    }
    if (!spinning) return undefined;
    const jitter = (Math.random() - 0.5) * step * 0.55;
    const to = 360 * 5 - (landed + 0.5) * step + jitter;
    // Recorded before the transition rather than on transitionend, because the
    // labels read it to decide which of them finish on the left half and need
    // flipping. Deciding that up front is what stops the text spinning round
    // with the disc and then jerking through 180 degrees once it stops.
    setTurn(to);
    g.style.transition = "none";
    g.style.transform = "rotate(0deg)";
    const id = window.setTimeout(() => {
      g.style.transition = "transform 4600ms cubic-bezier(.16,.84,.16,1)";
      g.style.transform = `rotate(${to}deg)`;
    }, 32);
    return () => {
      window.clearTimeout(id);
    };
  }, [spinning, landed, step]);

  const arc = (i: number): string => {
    const pt = (rad: number, deg: number): [string, string] => {
      const a = ((deg - 90) * Math.PI) / 180;
      return [(W_CX + rad * Math.cos(a)).toFixed(2), (W_CY + rad * Math.sin(a)).toFixed(2)];
    };
    const [x0, y0] = pt(W_R, i * step);
    const [x1, y1] = pt(W_R, (i + 1) * step);
    const [x2, y2] = pt(W_INNER, (i + 1) * step);
    const [x3, y3] = pt(W_INNER, i * step);
    const big = step > 180 ? 1 : 0;
    return `M${x0} ${y0}A${W_R} ${W_R} 0 ${big} 1 ${x1} ${y1}L${x2} ${y2}A${W_INNER} ${W_INNER} 0 ${big} 0 ${x3} ${y3}Z`;
  };

  const start = (event: PointerEvent<SVGSVGElement>): void => {
    if (spinning || onFlick === null) return;
    drag.current = { x: event.clientX, y: event.clientY, t: Date.now() };
  };
  const end = (event: PointerEvent<SVGSVGElement>): void => {
    const d = drag.current;
    drag.current = null;
    if (d === null || spinning || onFlick === null) return;
    if (Math.hypot(event.clientX - d.x, event.clientY - d.y) > 26 && Date.now() - d.t < 900) onFlick();
  };

  const pinW = 113 * PIN_SCALE;
  const pinH = 157 * PIN_SCALE;
  const pinX = W_CX - pinW / 2;
  const pinTip = W_CY - W_R + (W_R - W_INNER) * 0.4;

  return (
    <div className="dial">
      <svg
        viewBox={`0 ${pinTip - pinH - 10} 940 ${W_CY + W_R + 24 - (pinTip - pinH - 10)}`}
        role="img"
        aria-label={spinning ? "The wheel is spinning" : `A wheel of ${n} segments`}
        className={onFlick !== null ? "dial-grab" : undefined}
        data-run={spinning ? "true" : "false"}
        onPointerDown={start}
        onPointerUp={end}
        onPointerCancel={() => {
          drag.current = null;
        }}
      >
        <circle className="dial-base" cx={W_CX} cy={W_CY} r={W_R} fillOpacity={W_BASE_OP} />
        <g className="spin" ref={gRef}>
          {Array.from({ length: n }, (_, i) => (
            <path
              key={i}
              d={arc(i)}
              className={`seg${!spinning && landed === i ? " seg-win" : ""}`}
              fillOpacity={!spinning && landed === i ? undefined : n === 8 ? W_OPS[i] : i % 2 ? 0.15 : 0.05}
            />
          ))}
          {showLabels
            ? segments.slice(0, n).map((segment, i) => {
                // Inside the spinning group, so a label travels with its own
                // slice instead of hanging still over a moving face. Position
                // is the slice's own mid angle; the group carries it round.
                const mid = (i + 0.5) * step;
                const r = (W_R + W_INNER) / 2;
                const a = ((mid - 90) * Math.PI) / 180;
                const x = W_CX + r * Math.cos(a);
                const y = W_CY + r * Math.sin(a);
                return (
                  <text
                    key={segment.id}
                    className="seg-label"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="currentColor"
                    x={x}
                    y={y}
                    transform={`rotate(${labelTilt(mid, turn)} ${x} ${y})`}
                  >
                    {labelText(segment.short || segment.name)}
                  </text>
                );
              })
            : null}
        </g>
        <g transform={`translate(${pinX} ${pinTip - pinH}) scale(${PIN_SCALE})`}>
          <path className="pin" d={PIN_D} />
        </g>
      </svg>
    </div>
  );
}
